import { useEffect, useRef, useState } from 'react';
import type { AdminWhatsappSystemInstance } from '../../../domain/superadmin/types';
import { Button } from '../../../components/ui';
import Modal from '../../../components/ui/Modal';
import Spinner from '../../../components/ui/Spinner';

const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 15;
/** WhatsApp costuma renovar o QR ~20s; só trocamos a imagem nesse ritmo. */
const QR_REFRESH_MS = 18_000;

type Props = {
    isOpen: boolean;
    onClose: () => void;
    initialQr: string | null;
    onPoll: () => Promise<AdminWhatsappSystemInstance>;
    onConnected: () => void;
};

function toQrSrc(qr: string): string {
    return qr.startsWith('data:') ? qr : `data:image/png;base64,${qr}`;
}

export function WhatsappConnectQrModal({
    isOpen,
    onClose,
    initialQr,
    onPoll,
    onConnected,
}: Props) {
    const [qrSrc, setQrSrc] = useState<string | null>(null);
    const [timedOut, setTimedOut] = useState(false);
    const attemptsRef = useRef(0);
    const lastQrAtRef = useRef(0);
    const displayedRef = useRef<string | null>(null);
    const onPollRef = useRef(onPoll);
    const onConnectedRef = useRef(onConnected);
    const onCloseRef = useRef(onClose);
    onPollRef.current = onPoll;
    onConnectedRef.current = onConnected;
    onCloseRef.current = onClose;

    /** Troca o QR só depois que a imagem nova carrega — evita frame em branco. */
    const commitQr = (raw: string, force = false) => {
        const next = toQrSrc(raw);
        if (!force && next === displayedRef.current) return;
        if (!force && displayedRef.current && Date.now() - lastQrAtRef.current < QR_REFRESH_MS) {
            return;
        }

        const preload = new Image();
        preload.onload = () => {
            displayedRef.current = next;
            lastQrAtRef.current = Date.now();
            setQrSrc(next);
        };
        preload.src = next;
    };

    useEffect(() => {
        if (!isOpen) {
            setQrSrc(null);
            setTimedOut(false);
            attemptsRef.current = 0;
            lastQrAtRef.current = 0;
            displayedRef.current = null;
            return;
        }

        // initialQr chega após o POST de connect; só aplica se ainda não há QR na tela.
        if (initialQr && !displayedRef.current) {
            commitQr(initialQr, true);
        }
    }, [isOpen, initialQr]);

    useEffect(() => {
        if (!isOpen) return;

        let cancelled = false;
        let inFlight = false;

        const tick = async () => {
            if (cancelled || inFlight) return;
            if (attemptsRef.current >= MAX_POLL_ATTEMPTS) {
                setTimedOut(true);
                return;
            }

            inFlight = true;
            attemptsRef.current += 1;
            try {
                const instance = await onPollRef.current();
                if (cancelled) return;
                if (instance.qrBase64) commitQr(instance.qrBase64);
                if (instance.connected) {
                    onConnectedRef.current();
                    onCloseRef.current();
                }
            } finally {
                inFlight = false;
            }
        };

        const intervalId = window.setInterval(() => {
            void tick();
        }, POLL_INTERVAL_MS);

        return () => {
            cancelled = true;
            window.clearInterval(intervalId);
        };
    }, [isOpen]);

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="sm">
            <Modal.Header onClose={onClose}>Conectar WhatsApp</Modal.Header>
            <Modal.Body>
                <p className="mb-4 text-sm text-secondary">
                    Escaneie o QR Code no WhatsApp do celular da instância de sistema.
                </p>

                <div className="mx-auto flex h-56 w-56 items-center justify-center rounded-lg border border-subtle bg-white p-2">
                    {qrSrc ? (
                        <img
                            alt="QR Code WhatsApp"
                            src={qrSrc}
                            className="h-full w-full object-contain"
                        />
                    ) : (
                        <div className="flex flex-col items-center gap-2 text-sm text-muted">
                            <Spinner color="primary" size="lg" />
                            Gerando QR Code…
                        </div>
                    )}
                </div>

                {!timedOut && (
                    <p className="mt-4 text-center text-xs text-muted">Aguardando conexão…</p>
                )}

                {timedOut && (
                    <p className="mt-4 text-center text-sm text-destructive">
                        Tempo esgotado. Feche e tente conectar novamente.
                    </p>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button type="button" variant="secondary" onClick={onClose} className="w-full sm:w-auto">
                    {timedOut ? 'Fechar' : 'Cancelar'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
